/**
 * LODEN — component-facets.js
 * <collection-facets>  Filtering, sorting and progressive pagination.
 * <price-range>        Dual-handle price slider bound to two number inputs.
 * <load-more-pagination>
 *
 * Everything here is an interception of a working form or link. The facet form
 * is a real GET form, "load more" is a real link to page 2, and sorting is a
 * real select inside that form. With JavaScript disabled every one of them
 * still produces the correct page — this file only avoids the reload.
 */
(function () {
  'use strict';

  const { Element, utils, a11y, emit } = window.Aurelia;

  /* =====================================================================
     Facets
     ===================================================================== */
  class CollectionFacets extends Element {
    mount() {
      this.sectionId = this.dataset.sectionId;
      this.form = this.querySelector('[data-facet-form]');
      this.results = this.querySelector('[data-facet-results]');
      this.countTarget = this.querySelector('[data-product-count]');

      if (!this.form || !this.results) return;

      this.bind();

      // Back/forward must restore the filtered view, not a stale one.
      this.listen(window, 'popstate', () => this.apply(window.location.href, { push: false }));
    }

    bind() {
      // Checkboxes apply immediately; a separate "apply" step is an extra
      // click that every shopper has to learn.
      this.listen(this.form, 'change', (event) => {
        if (!event.target.matches('[data-facet-input], [data-facet-sort]')) return;
        this.submit();
      });

      // Price inputs are debounced — applying on every keystroke would fire a
      // request per digit.
      const debouncedSubmit = utils.debounce(() => this.submit(), 500);
      this.listen(this.form, 'input', (event) => {
        if (!event.target.matches('[data-price-min], [data-price-max]')) return;
        debouncedSubmit();
      });

      this.listen(this.form, 'submit', (event) => {
        event.preventDefault();
        this.submit();
      });

      // Active-filter chips and "clear all" are plain links; follow them
      // through the same AJAX path rather than reloading.
      this.listen(this, 'click', (event) => {
        const link = event.target.closest('[data-facet-remove], [data-facet-clear]');
        if (!link) return;
        event.preventDefault();
        this.apply(link.href);
      });

      // Grid / list layout toggle.
      this.$$('[data-layout]').forEach((button) => {
        this.listen(button, 'click', () => this.setLayout(button.dataset.layout));
      });
    }

    submit() {
      const params = new URLSearchParams(new FormData(this.form));

      // Empty values would otherwise produce `?filter.v.price.gte=` in the URL.
      Array.from(params.entries()).forEach(([key, value]) => {
        if (value === '') params.delete(key);
      });

      const url = `${this.form.action}?${params.toString()}`;
      this.apply(url);
    }

    async apply(url, { push = true } = {}) {
      this.setBusy(true);

      if (this.abortController) this.abortController.abort();
      this.abortController = new AbortController();

      try {
        const html = await window.Aurelia.fetchSection(url, {
          sectionId: this.sectionId,
          signal: this.abortController.signal,
        });

        const parsed = utils.parseHTML(html);

        const freshResults = parsed.querySelector('[data-facet-results]');
        if (freshResults) this.results.innerHTML = freshResults.innerHTML;

        // The facet list itself changes: counts move and values become
        // unavailable as other filters narrow the set.
        const freshForm = parsed.querySelector('[data-facet-form]');
        if (freshForm) this.replaceFacets(freshForm);

        const freshCount = parsed.querySelector('[data-product-count]');
        if (freshCount && this.countTarget) {
          this.countTarget.textContent = freshCount.textContent;
          a11y.announce(freshCount.textContent.trim());
        }

        if (push) window.history.pushState({ facets: true }, '', url);

        emit('content:appended', { target: this.results });

        // Return the customer to the top of the results, not the top of the
        // page — they have not left the collection.
        const top = this.results.getBoundingClientRect().top + window.scrollY;
        if (window.scrollY > top) window.scrollTo({ top: top - 100, behavior: 'smooth' });
      } catch (error) {
        if (error.name === 'AbortError') return;
        window.location.href = url;
      } finally {
        this.setBusy(false);
      }
    }

    /**
     * Swaps facet contents while preserving which panels the customer had
     * open — collapsing every filter group on each click is disorienting.
     */
    replaceFacets(freshForm) {
      const openState = new Map();
      this.form.querySelectorAll('details.facet').forEach((details, index) => {
        openState.set(index, details.open);
      });

      const activeId = document.activeElement ? document.activeElement.id : null;

      this.form.innerHTML = freshForm.innerHTML;

      this.form.querySelectorAll('details.facet').forEach((details, index) => {
        if (openState.has(index)) details.open = openState.get(index);
      });

      if (activeId) {
        const restored = this.form.querySelector(`#${CSS.escape(activeId)}`);
        if (restored) restored.focus({ preventScroll: true });
      }
    }

    setLayout(layout) {
      const grid = this.querySelector('.product-grid');
      if (!grid) return;

      grid.classList.toggle('product-grid--list', layout === 'list');
      grid.querySelectorAll('.product-card').forEach((card) => {
        card.classList.toggle('product-card--list', layout === 'list');
      });

      this.$$('[data-layout]').forEach((button) => {
        const isActive = button.dataset.layout === layout;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });

      try {
        localStorage.setItem('aurelia:collection-layout', layout);
      } catch (error) {
        /* Storage unavailable — the choice simply does not persist. */
      }
    }

    setBusy(isBusy) {
      this.results.setAttribute('aria-busy', String(isBusy));
      this.classList.toggle('is-loading', isBusy);
    }
  }

  window.Aurelia.define('collection-facets', CollectionFacets);

  /* =====================================================================
     Optional sub-components — deferred
     <price-range> exists only when the collection exposes a price filter, and
     <load-more-pagination> only when the merchant picked progressive
     pagination. Loading them on presence keeps them off every other page.
     Re-checked on `content:appended`, which fires after a facet render swaps
     the filter form and can introduce a price slider that was not there
     before. Both loads are idempotent — the loader caches one promise per
     module — so re-checking costs nothing.
     ===================================================================== */
  function loadOptional() {
    if (document.querySelector('price-range')) {
      window.Aurelia.load('price-range').catch(() => {});
    }
    if (document.querySelector('load-more-pagination')) {
      window.Aurelia.load('load-more').catch(() => {});
    }
  }

  loadOptional();
  window.Aurelia.on('content:appended', loadOptional);
})();
